import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CountryProvider } from "./contexts/CountryContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import CountryWelcome from "./components/CountryWelcome";
import PreviewToProdBanner from "./components/PreviewToProdBanner";
import AutoLoginGateProvider from "./components/auth/AutoLoginGate";
import { Toaster } from "./components/ui/sonner";
import { Download, X } from "lucide-react";
import { trackPageView } from "./utils/analytics";
import { captureMarketOverrideFromURL } from "./utils/marketOverride";

// Pages
import LandingPage from "./pages/LandingPage";
import ClaimProPage from "./pages/ClaimProPage";
import ShortClaimRedirect from "./pages/ShortClaimRedirect";
import RoleLandingPage from "./pages/RoleLandingPage";
import CityProsDirectoryPage from "./pages/CityProsDirectoryPage";
import RegionHubPage from "./pages/RegionHubPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./components/AuthCallback";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import BuyerDashboard from "./pages/BuyerDashboard";
import SellerDashboard from "./pages/SellerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import CreatePropertyPage from "./pages/CreatePropertyPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import FavoritesPage from "./pages/FavoritesPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import TermsPage from "./pages/TermsPage";
import ProfessionalDashboard from "./pages/ProfessionalDashboard";
import ProfessionalOnboardingPage from "./pages/ProfessionalOnboardingPage";
import FindProfessionalPage from "./pages/FindProfessionalPage";
import AgentDashboard from "./pages/AgentDashboard";
import MortgageGuidePage from "./pages/MortgageGuidePage";
import BuyingGuidePage from "./pages/BuyingGuidePage";
import SellingGuidePage from "./pages/SellingGuidePage";
import TenantRentingGuidePage from "./pages/TenantRentingGuidePage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import CountryPropertiesPage from "./pages/CountryPropertiesPage";
import AgentProfilePage from "./pages/AgentProfilePage";
import MyAccountPage from "./pages/MyAccountPage";
import PhotoBridgePage from "./pages/PhotoBridgePage";
import TenantDashboard from "./pages/TenantDashboard";
import ReferralLandingPage from "./pages/ReferralLandingPage";
import AgentLandingPage from "./pages/AgentLandingPage";
import FreeValuationPage from "./pages/FreeValuationPage";
import InvitePage from "./pages/InvitePage";
import ForAgentsPage from "./pages/ForAgentsPage";
import { VsRightmove, Vs99Acres, VsRealtor } from "./pages/VsRedirects";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import SavedSearchMatchesPage from "./pages/SavedSearchMatchesPage";
import CartRecoveryPage from "./pages/CartRecoveryPage";
import InTheNewsPage from "./pages/InTheNewsPage";
import AffiliatePromoPage from "./pages/AffiliatePromoPage";
import EstateAgentsAILanding from "./pages/personas/EstateAgentsAILanding";
import MortgageBrokersLanding from "./pages/personas/MortgageBrokersLanding";
import ProfessionalJoinPage from "./pages/ProfessionalJoinPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import PersonaLandingPage from "./pages/PersonaLandingPage";
import AIFloorPlanLanding from "./pages/tools/AIFloorPlanLanding";
import AIValuationLanding from "./pages/tools/AIValuationLanding";
import AIScriptGeneratorLanding from "./pages/tools/AIScriptGeneratorLanding";
import TenancyTemplateLanding from "./pages/agreements/TenancyTemplateLanding";
import LocationLandingPage from "./pages/LocationLandingPage";
import LocationsHubPage from "./pages/LocationsHubPage";
import PressReleasePage from "./pages/PressReleasePage";
import PulsePage from "./pages/PulsePage";
import GrowthReportPage from "./pages/GrowthReportPage";
import NetworkAgreementPage from "./pages/NetworkAgreementPage";
import FloatingAIChat from "./components/FloatingAIChat";
import ChatDialog from "./components/ChatDialog";
import ExitIntentModal from "./components/ExitIntentModal";

// Capture admin/owner market-test override (`?test=IN`) at boot so it
// applies to the very first compliance/geo call — must run before any
// detection effect fires.
captureMarketOverrideFromURL();

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    const roleRedirects = { admin: '/admin', seller: '/seller', agent: '/agent', professional: '/professional', tenant: '/tenant', investor: '/buyer' };
    return <Navigate to={roleRedirects[user.role] || '/buyer'} replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'seller') return <Navigate to="/seller" replace />;
    if (user.role === 'agent') return <Navigate to="/agent" replace />;
    if (user.role === 'professional') return <Navigate to="/professional" replace />;
    if (user.role === 'tenant') return <Navigate to="/tenant" replace />;
    return <Navigate to="/buyer" replace />;
  }

  return children;
};

const BUYER_ADMIN_ROLES = ['buyer', 'investor', 'admin'];
const TENANT_ADMIN_ROLES = ['tenant', 'admin'];
const SELLER_ADMIN_ROLES = ['seller', 'admin'];
const SELLER_AGENT_ADMIN_ROLES = ['seller', 'agent', 'admin'];
const AGENT_ADMIN_ROLES = ['agent', 'admin'];
const PROFESSIONAL_ADMIN_ROLES = ['professional', 'admin'];
const ADMIN_ONLY_ROLES = ['admin'];

// Track page views on route change
const PageViewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

function AppRoutes() {
  // CRITICAL: Synchronous Emergent OAuth fragment handler — runs BEFORE any route
  // matches, preventing the AuthProvider checkAuth race per playbook.
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
    <PageViewTracker />
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pulse" element={<PulsePage />} />
      <Route path="/pulse/success" element={<PaymentSuccessPage />} />
      <Route path="/growth-report/:slug" element={<GrowthReportPage />} />
      <Route path="/network/agreement" element={<NetworkAgreementPage />} />
      <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
      <Route path="/properties/:countrySlug" element={<ProtectedRoute><CountryPropertiesPage /></ProtectedRoute>} />
      <Route path="/saved-searches/:id" element={<ProtectedRoute><SavedSearchMatchesPage /></ProtectedRoute>} />
      <Route path="/cart-recovery" element={<CartRecoveryPage />} />
      <Route path="/in-the-news" element={<InTheNewsPage />} />
      <Route path="/press-room" element={<InTheNewsPage />} />
      <Route path="/affiliate" element={<AffiliatePromoPage />} />
      <Route path="/affiliate-program" element={<AffiliatePromoPage />} />
      <Route path="/agents/ai-tools" element={<EstateAgentsAILanding />} />
      <Route path="/agents/:country" element={<AgentLandingPage />} />
      <Route path="/agents/welcome" element={<PaymentSuccessPage />} />
      <Route path="/free-valuation" element={<FreeValuationPage />} />
      <Route path="/claim/:place_id" element={<ClaimProPage />} />
      {/* Personalised short-link for WhatsApp pro-blast. See ShortClaimRedirect.js */}
      <Route path="/p/:code" element={<ShortClaimRedirect />} />
      <Route path="/for/:slug" element={<RoleLandingPage />} />
      <Route path="/pros/:city" element={<CityProsDirectoryPage />} />
      <Route path="/region/:slug" element={<RegionHubPage />} />
      <Route path="/pulse-report/success" element={<PaymentSuccessPage />} />
      <Route path="/for-mortgage-brokers" element={<MortgageBrokersLanding />} />
      <Route path="/professional/join" element={<ProfessionalJoinPage />} />
      <Route path="/join-panel" element={<ProfessionalJoinPage />} />
      <Route path="/property/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/mortgage-guide" element={<MortgageGuidePage />} />
      <Route path="/buying-guide" element={<BuyingGuidePage />} />
      <Route path="/selling-guide" element={<SellingGuidePage />} />
      <Route path="/tenant-guide" element={<TenantRentingGuidePage />} />
      <Route path="/tenant-renting-guide" element={<TenantRentingGuidePage />} />
      <Route path="/agent/:slug" element={<AgentProfilePage />} />
      <Route path="/photo-bridge/:sessionId" element={<PhotoBridgePage />} />
      <Route path="/r/:code" element={<ReferralLandingPage />} />
      <Route path="/for-agents" element={<ForAgentsPage />} />
      <Route path="/find-a-professional" element={<FindProfessionalPage />} />
      <Route path="/vs-rightmove" element={<VsRightmove />} />
      <Route path="/vs-99acres" element={<Vs99Acres />} />
      <Route path="/vs-realtor" element={<VsRealtor />} />
      <Route path="/subscribe/success" element={<SubscriptionSuccessPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/agents" element={<PersonaLandingPage persona="agents" />} />
      <Route path="/brokers" element={<PersonaLandingPage persona="brokers" />} />
      <Route path="/tenants" element={<PersonaLandingPage persona="tenants" />} />
      <Route path="/sellers" element={<PersonaLandingPage persona="sellers" />} />
      <Route path="/tools/ai-floor-plan" element={<AIFloorPlanLanding />} />
      <Route path="/tools/ai-valuation" element={<AIValuationLanding />} />
      <Route path="/tools/ai-script-generator" element={<AIScriptGeneratorLanding />} />
      <Route path="/agreements/tenancy-template" element={<TenancyTemplateLanding />} />
      <Route path="/locations" element={<LocationsHubPage />} />
      <Route path="/properties-in/:slug" element={<LocationLandingPage />} />
      <Route path="/press" element={<PressReleasePage />} />
      <Route path="/press/:slug" element={<PressReleasePage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      
      {/* Buyer Routes */}
      <Route path="/buyer" element={<ProtectedRoute allowedRoles={BUYER_ADMIN_ROLES}><BuyerDashboard /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
      
      {/* Tenant Routes */}
      <Route path="/tenant" element={<ProtectedRoute allowedRoles={TENANT_ADMIN_ROLES}><TenantDashboard /></ProtectedRoute>} />
      
      {/* Seller Routes */}
      <Route path="/seller" element={<ProtectedRoute allowedRoles={SELLER_ADMIN_ROLES}><SellerDashboard /></ProtectedRoute>} />
      <Route path="/seller/create" element={<ProtectedRoute allowedRoles={SELLER_AGENT_ADMIN_ROLES}><CreatePropertyPage /></ProtectedRoute>} />
      <Route path="/seller/edit/:id" element={<ProtectedRoute allowedRoles={SELLER_AGENT_ADMIN_ROLES}><CreatePropertyPage /></ProtectedRoute>} />
      
      {/* Professional Routes */}
      <Route path="/professional" element={<ProtectedRoute allowedRoles={PROFESSIONAL_ADMIN_ROLES}><ProfessionalDashboard /></ProtectedRoute>} />
      <Route path="/professional/onboard" element={<ProtectedRoute allowedRoles={PROFESSIONAL_ADMIN_ROLES}><ProfessionalOnboardingPage /></ProtectedRoute>} />
      
      {/* Agent Routes */}
      <Route path="/agent" element={<ProtectedRoute allowedRoles={AGENT_ADMIN_ROLES}><AgentDashboard /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={ADMIN_ONLY_ROLES}><AdminDashboard /></ProtectedRoute>} />
      
      {/* Payment Routes */}
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      
      {/* My Account - all logged-in users */}
      <Route path="/my-account" element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>} />
      <Route path="/invite" element={<ProtectedRoute><InvitePage /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// PWA Install Prompt
const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only show if not dismissed before
      if (!localStorage.getItem('pwa-dismissed')) {
        setShowBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-dismissed', '1');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-white border border-[#E07A3A]/20 shadow-2xl z-[9999] p-4 animate-in slide-in-from-bottom-4" data-testid="pwa-install-banner">
      <button onClick={handleDismiss} className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-[#FEF3EC] flex items-center justify-center flex-shrink-0">
          <span className="text-[#E07A3A] font-['Playfair_Display'] text-xl font-bold">L</span>
        </div>
        <div>
          <p className="text-[#1A1A1A] font-semibold text-sm">Install Shopperden</p>
          <p className="text-[#1A1A1A]/50 text-xs">Add to home screen for quick access</p>
        </div>
      </div>
      <button onClick={handleInstall} className="w-full mt-3 bg-[#E07A3A] text-white py-2.5 font-semibold text-sm tracking-wider hover:bg-[#C4652E] transition-colors flex items-center justify-center gap-2" data-testid="pwa-install-btn">
        <Download className="h-4 w-4" />INSTALL APP
      </button>
    </div>
  );
};

function _RouteAwareOverlays() {
  const location = useLocation();
  // Suppress country welcome on landing pages reached via outreach links —
  // the pro doesn't need a country picker, the lead's country is implicit.
  const suppressCountryWelcome =
    location.pathname.startsWith('/claim/') ||
    location.pathname.startsWith('/for/') ||
    location.pathname.startsWith('/pros/') ||
    location.pathname.startsWith('/region/') ||
    location.pathname.startsWith('/cart-recovery') ||
    location.pathname.startsWith('/blog') ||
    location.pathname.startsWith('/press') ||
    location.pathname.startsWith('/in-the-news') ||
    location.pathname.startsWith('/affiliate');
  return (
    <>
      <ChatDialog />
      {!suppressCountryWelcome && <CountryWelcome />}
      <PreviewToProdBanner />
      <Toaster position="top-right" richColors />
      <InstallBanner />
      <FloatingAIChat />
      <ExitIntentModal />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CountryProvider>
        <LanguageProvider>
        <BrowserRouter>
          <AutoLoginGateProvider>
            <AppRoutes />
            <_RouteAwareOverlays />
          </AutoLoginGateProvider>
        </BrowserRouter>
        </LanguageProvider>
        </CountryProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
